import {    HttpRequestError,
            toHttpRequest, 
            EventBus,
            ConfigService,
            ServerConnector,
            DialogService, 
            DialogEntity,
            MessageEntity,
            EmotionTypeEnum,
            WidgetCommandEnum,
            UserMessageTypeEnum,
            ErrorMessageTypeEnum,
            ConversationSessionEntity,
            MessageFactory,
            MessageService,
            NotificationEntity,
            BotInitializationService}                      from '../../../';


export interface IConversationEntityActions {
    displayIntroductionSentence(storeFunctions: any, payload: any): any;
    sendNotificationCommand(storeFunctions: any, payload: any): any;
    sendMessage(storeFunctions: any, payload: any): any;
    requestMessageAnswer(storeFunctions: any, payload: any): any;
    logSatisfaction(storeFunctions: any, payload: any): any;
    logDissatisfaction(storeFunctions: any, payload: any): any;
    displaySatisfactionFeedbackBotMessage(storeFunctions: any, payload: any): any;
    resetNotification(storeFunctions: any): any;
    markNotificationAsSeen(storeFunctions: any, payload : any): any;
    updateCitations(context: any, payload: { botName: string; answerCounts: number[] }): Promise<void>;
}

export const ConversationEntityActions: IConversationEntityActions = {

    /**
     * Action definition triggered at conversation session initialization. Display presentation or welcome back message.
     * 
     * @param storeFunctions - Commit and Dispatch functions to apply changes into the store.
     * @param payload - The current session information state.
     */
    displayIntroductionSentence({ commit, dispatch }, { sessionInfo, isRefresh }) {
        // the introduction sentence is not mandatory it could depend on the configuration params
        const introductionSentence: string | null = ConfigService.getIntroductionSentence();
        let dialog: DialogEntity;
        
        if (!isRefresh && introductionSentence && introductionSentence.length > 0 && introductionSentence.replace(/\s/g, '').length > 0) {
            dialog = MessageFactory.buildNewDialogEntity(MessageFactory.buildIntroductionMessage(introductionSentence, sessionInfo));
        } else {
            dialog = MessageFactory.buildNewDialogEntity();
        }
        
        // get initialized conversation
        const conversationSession: ConversationSessionEntity = new ConversationSessionEntity(sessionInfo.conversationId, dialog);
        
        // change conversation state
        commit('INIT_CONVERSATION', conversationSession);

        // initialize framework content
        dispatch('conversationSession/toggleIntroductionSentenceShownState', {}, { root: true });
    },

    /**
     * Action definition triggered at conversation session initialization. Send the configured initPost
     * command to BotServer.
     * 
     * @param storeFunctions - Dispatch function to trigger send command to BotServer.
     * @param payload - The chatbox widget configuration retrieved from server and the session data.
     */
    async sendNotificationCommand({ dispatch }, { sessionInfo }) {
        const textContent: string | null = ConfigService.getBotNotificationCommand();
        if (textContent && textContent.length > 0 && textContent.replace(/\s/g, '').length > 0) {
            // Retrieves the message to send
            const updateLoadingState: boolean = false;
            const waitForAnswer: boolean = true;
            const msgType: UserMessageTypeEnum = UserMessageTypeEnum.WIDGET_COMMAND;
            const msgAttachments: [] = [];

            // initialize framework content
            dispatch('messages/sendMessage',
                        { sessionInfo, updateLoadingState, waitForAnswer, msgType, textContent, msgAttachments }, // payload data
                        { root: true }); // uses root reference
        }
    },

    /**
     * Action definition triggered to send a message to BotServer.
     * 
     * @param storeFunctions - Commit and Dispatch functions to apply changes into the store.
     * @param payload - The session data, the message and the possible attachments to send, and request flow controls indicators 
     *                  (updateLoadingState and waitForAnswer).
     */
    async sendMessage({ commit, dispatch }, { sessionInfo, msgType, msgText, textContent, msgAttachments, updateLoadingState, 
                                              waitForAnswer, isRetry = false }) {
            if (textContent && textContent.length > 0 && textContent.replace(/\s/g, '').length > 0) {

            // Widget commands must not be tagged when conversation logs are not recorded 
            if (!ConfigService.isLogConversationActivated && textContent[0] !== '/') {
                // Only add the rule ignoreLog when it's a user message. 
                // It will automatically propagate if a user clicks on message's buttons
                if (msgType === UserMessageTypeEnum.USER_MSG) {
                    textContent = "##ignoreLog=true##" + textContent;
                }
            }

            const message: MessageEntity = MessageFactory.buildMessageToSend(sessionInfo, msgType, msgText, msgAttachments);

            if (!isRetry && (msgType === UserMessageTypeEnum.USER_MSG || msgType === UserMessageTypeEnum.USER_SELECTED_SUGGESTION)) {
                // add message to the dialog entity
                commit('REGISTER_MESSAGE_SENT', message);
            }

            if (!isRetry && updateLoadingState) {
                // set loading animation in chatbot conversation window
                commit('SEND_MESSAGE_REQUEST_IN_PROGRESS');
                // play thinking emotion
                dispatch('conversationSession/playEmotion', EmotionTypeEnum.THINK, { root: true });
            }
            
            // Set URL and parameters to be sent to the chatbot backend
            const urlPath: string = `/line/synapse/bot/${ConfigService.synapseBotId}/conversation/${sessionInfo.conversationId}/message`;
            const params: any = {   from: message.from,
                                    data: [ {Key: 'botName', Value: sessionInfo.botName},
                                            {Key: 'userId', Value: sessionInfo.guestId}, 
                                            {Key: 'url', Value: sessionInfo.originURL}],
                                    text: textContent,
                                    watermark: message.watermark,
                                    conversationId: message.conversationId,
                                    time: message.time,
                                    attachments: message.attachments
                                };

            // Add all entries from window.botSettings.data to params.data
            const additionalData = ConfigService.widgetConfig?.params?.get('data');
            if (additionalData) {
                Object.entries(additionalData).forEach(([key, value]) =>
                    params.data.push({ Key: key, Value: value }));
            }

            // tslint:disable-next-line: prefer-const
            const [err, results]: [HttpRequestError | undefined, any] = await toHttpRequest(ServerConnector.getBotServerConnectorInstance()
                                                                                                           .post(urlPath, params));
            if (err && msgType != UserMessageTypeEnum.WIDGET_COMMAND && (err.errCode !== '5' || err.httpCode !== 408)) {
                // register BotServer error notification
                commit('REGISTER_ON_ERROR_SERVER_NOTIFICATION', { err });
                // initialize a new conversation session
                dispatch('conversationSession/playEmotion', EmotionTypeEnum.SADNESS, { root: true });
                // If there's been a network interruption
                if (err.httpCode == 500) {
                    // Sends a message to the user to warn of the network failure
                    const notificationMessage: MessageEntity = MessageFactory.buildErrorMessage(ConfigService.getNetworkFailureMessage(), sessionInfo, ErrorMessageTypeEnum.ERROR_500);
                    commit('REGISTER_MESSAGE_SENT', notificationMessage);
                } else {
                    // Sends a message to the user to warn of the error
                    const notificationMessage: MessageEntity = MessageFactory.buildErrorMessage(ConfigService.getErrorMessage(), sessionInfo, ErrorMessageTypeEnum.DEFAULT);
                    commit('REGISTER_MESSAGE_SENT', notificationMessage);
                }
            } else if (results && !Number.isNaN(Number(results.result)) && results.result === 0) {
                if (msgType === UserMessageTypeEnum.WIDGET_COMMAND) {
                    // to prevent command message appears into conversation panel update manually
                    // the next watermark when message type is a command
                    const nextMsgWatermark = ++sessionInfo.nextMsgWatermark;
                    dispatch('conversationSession/setConversationNextMessageWatermark', nextMsgWatermark, { root: true });
                }

                // start getting expected message answer
                if (msgType !== UserMessageTypeEnum.WIDGET_COMMAND && waitForAnswer) {
                    setTimeout(() => dispatch('messages/requestMessageAnswer', { sessionInfo, isRetry }, { root: true }), ConfigService.getPoolBotAnswerRequestPeriod());
                } else {
                    let commandText: string | undefined = undefined;

                    // Apply only on commands without bot response
                    // We simulate an aswer here to give feedback
                    switch(msgText) {
                        case WidgetCommandEnum.RELOAD : {
                            commandText = "[Chatbot config has been reloaded]"
                            break;
                        }
                        default : null;
                    }

                    if (commandText) {
                        // REGISTER_MESSAGES_BOT_ANSWER_SUCCESS needs an array of MessageEntity
                        const commandMessage: MessageEntity[] = [MessageFactory.buildCommandMessage(sessionInfo, commandText)];
                        // This timeout is used to simulate the waiting time when the bot is responding something
                        setTimeout(() => commit('REGISTER_MESSAGES_BOT_ANSWER_SUCCESS', commandMessage), ConfigService.getFakeBotAnswerWaitingTime());
                    }
                }
            }
        }
    },

    /**
     * Action definition triggered when a message has been sent and an answer is expected.
     * 
     * @param storeFunctions - Commit and Dispatch functions to apply changes into the store.
     * @param payload - The chatbox widget configuration retrieved from server and the session data.
     */
    async requestMessageAnswer({ commit, dispatch }, { sessionInfo, isRetry = false, remainingRetries = ConfigService.getPoolBotAnswerRequestNbRetries() }) {

        const urlPath: string = `/line/synapse/bot/${ConfigService.synapseBotId}/conversation/${sessionInfo.conversationId}/messages?watermark=${sessionInfo.nextMsgWatermark}&{}`;

        // tslint:disable-next-line: prefer-const
        const [err, results]: [HttpRequestError | undefined, any] = await toHttpRequest(ServerConnector.getBotServerConnectorInstance().get(urlPath));

       if (err && err.httpCode !== 200) {
            BotInitializationService.stopNotificationPolling()
            commit('REGISTER_ON_ERROR_SERVER_NOTIFICATION', { err });
            // initialize a new conversation session
            dispatch('conversationSession/playEmotion', EmotionTypeEnum.SADNESS, { root: true });

        } else if (results && results.result && results.result.activities && results.result.activities.length > 0) {
            const notifs : NotificationEntity[] = []
            const rawNotifs = results.result.activities.filter((e: any) => e.messageType === 5)
            for (const rawNotif  of  rawNotifs){
                notifs.push(MessageFactory.buildNotificationEntity(rawNotif))
            }
            commit('ADD_TO_NOTIFICATIONS', notifs)
            if (!Number.isNaN(Number(results.result.watermark))) {
                const nextMsgWatermark: number = results.result.watermark;
                dispatch('conversationSession/setConversationNextMessageWatermark', nextMsgWatermark, { root: true }); 
            }

            if (!isRetry) {
                const messages: MessageEntity[] = DialogService.getMessagesFromBotAnswer(results.result.activities);    
                // Update the number of citations for each message
                messages.forEach((message: MessageEntity) => {
                    const citationCount = DialogService.getUniqueCitationsFromHtml(message.text);
                    console.log('citationCount', citationCount);
                    dispatch('updateCitations', { botName: 'myBot', answerCounts: citationCount });

                });



                const emotionToPlay: string | undefined = DialogService.getMessagesEmotionToPlay(messages);
                if (emotionToPlay) {
                    dispatch('conversationSession/playEmotion', emotionToPlay, { root: true }); 
                } else {
                    // initialize a new conversation session
                    dispatch('conversationSession/playEmotion', EmotionTypeEnum.NEUTRAL, { root: true });
                }
                commit('REGISTER_MESSAGES_BOT_ANSWER_SUCCESS', messages);
            }
        } else if (remainingRetries > 0) {
            // retry getting expected message answer
            remainingRetries--;
            setTimeout(() => dispatch('messages/requestMessageAnswer', { sessionInfo, isRetry, remainingRetries }, { root: true }), ConfigService.getPoolBotAnswerRequestPeriod());
        }
    },

    /**
     * Action definition triggered when a user clicks on satisfaction OK button.
     * 
     * @param storeFunctions - Commit and Dispatch functions to apply changes into the store.
     * @param payload - The chatbox widget configuration retrieved from server and the session data.
     */
     async logSatisfaction({ commit, dispatch }, { txtAnswer, sessionInfo, buttonKey, satisfactionMsgId, satisfactionBtnId, finalMessage, watermark }) {

        // set loading animation in chatbot conversation window
        commit('SEND_MESSAGE_SAT_REQUEST_IN_PROGRESS');

        const parsedKey: any = MessageService.parseHiddenDatasInBotMessageKey(buttonKey);
        const urlPath: string = `/line/synapse/bot/${ConfigService.synapseBotId}/conversationId/${sessionInfo.conversationId}/logsatisfactionclarification`;
        const param: any = {
                                watermark: watermark,
                                from: ConfigService.getBotUserOrigin(),
                                data: [ {Key: 'txtAnswer', Value: txtAnswer},
                                        {Key: 'botName', Value: sessionInfo.botName},
                                        {Key: 'userId', Value: sessionInfo.guestId},
                                        {Key: 'url', Value: sessionInfo.originURL},
                                        {Key: 'satisfaction', Value: 1},
                                        {Key: 'clarification', Value: null},
                                        {Key: 'question', Value: parsedKey.question},
                                        {Key: 'idAnswer', Value: parsedKey.idAnswer}],
                           };
 
        // tslint:disable-next-line: prefer-const
        const [err, results]: [HttpRequestError | undefined, any] = await toHttpRequest(ServerConnector.getBotServerConnectorInstance().post(urlPath,param));

        if (err && (err.errCode !== '5' || err.httpCode !== 408) ) {        
            commit('REGISTER_ON_ERROR_SERVER_NOTIFICATION', { err });
            EventBus.$emit('SATISFACTION_BUTTON_STATE_ON_ERROR');
            dispatch('conversationSession/playEmotion', EmotionTypeEnum.SADNESS, { root: true });
        } else {
            if (results.result == 0) {
                EventBus.$emit('SATISFACTION_BUTTON_STATE_SUCCESSED');
                dispatch('conversationSession/playEmotion', EmotionTypeEnum.JOY, { root: true });
                if (finalMessage !== undefined) {
                    dispatch('messages/displaySatisfactionFeedbackBotMessage', { sessionInfo, finalMessage, watermark }, { root: true });
                }
                commit('REGISTER_SATISFACTION_FEEDBACK_DONE', { messageId: satisfactionMsgId, satisfactionId: satisfactionBtnId });
            } else {
                commit('REGISTER_ON_ERROR_SERVER_NOTIFICATION', { err });
                EventBus.$emit('SATISFACTION_BUTTON_STATE_ON_ERROR');
                dispatch('conversationSession/playEmotion', EmotionTypeEnum.SADNESS, { root: true });
            }
        } 
    },

    /**
     * Action definition triggered when a user clicks on dissatisfaction button and/or reason (clarification).
     * 
     * @param storeFunctions - Commit and Dispatch functions to apply changes into the store.
     * @param payload - The chatbox widget configuration retrieved from server and the session data.
     */
    async logDissatisfaction({ commit, dispatch }, { txtAnswer, sessionInfo, buttonKey, clarification, satisfactionMsgId, satisfactionBtnId, finalMessage, watermark }) {

        // set loading animation in chatbot conversation window
        commit('SEND_MESSAGE_SAT_REQUEST_IN_PROGRESS');

        const parsedKey:any = MessageService.parseHiddenDatasInBotMessageKey(buttonKey);
        const urlPath: string = `/line/synapse/bot/${ConfigService.synapseBotId}/conversationId/${sessionInfo.conversationId}/logsatisfactionclarification`;
        const param: any = {
                                watermark: watermark,
                                from: ConfigService.getBotUserOrigin(),
                                data: [ {Key: 'txtAnswer', Value: txtAnswer},
                                        {Key: 'botName', Value: sessionInfo.botName},
                                        {Key: 'userId', Value: sessionInfo.guestId},
                                        {Key: 'url', Value: sessionInfo.originURL},
                                        {Key: 'satisfaction', Value: 0},
                                        {Key: 'clarification', Value: clarification},
                                        {Key: 'question', Value: parsedKey.question},
                                        {Key: 'idAnswer', Value: parsedKey.idAnswer}],
                           };

        // tslint:disable-next-line: prefer-const
        const [err, results]: [HttpRequestError | undefined, any] = await toHttpRequest(ServerConnector.getBotServerConnectorInstance().post(urlPath,param));

        if (err && (err.errCode !== '5' || err.httpCode !== 408) ) {        
            commit('REGISTER_ON_ERROR_SERVER_NOTIFICATION', { err });
            EventBus.$emit('DISSATISFACTION_BUTTON_STATE_ON_ERROR');
        } else {
            if (results.result == 0) {
                EventBus.$emit('DISSATISFACTION_BUTTON_STATE_SUCCESSED');
                if (finalMessage !== undefined) {
                    dispatch('messages/displaySatisfactionFeedbackBotMessage', { sessionInfo, finalMessage, watermark }, { root: true });
                }
                commit('REGISTER_SATISFACTION_FEEDBACK_DONE', { messageId: satisfactionMsgId, satisfactionId: satisfactionBtnId });
            } else {
                commit('REGISTER_ON_ERROR_SERVER_NOTIFICATION', { err });
                EventBus.$emit('DISSATISFACTION_BUTTON_STATE_ON_ERROR');
            }
        }
        dispatch('conversationSession/playEmotion', EmotionTypeEnum.SADNESS, { root: true });
    },

    /**
     * Action definition triggered at conversation session initialization. Display presentation or welcome back message.
     * 
     * @param storeFunctions - Commit and Dispatch functions to apply changes into the store.
     * @param payload - The current session information state.
     */
    displaySatisfactionFeedbackBotMessage({ commit }, { sessionInfo, finalMessage, watermark }) {
        const message: MessageEntity = MessageFactory.buildMessageEntityFromBotSatisfactionFeedback(finalMessage, sessionInfo, watermark);
        // add message to the dialog entity
        commit('REGISTER_MESSAGE_SENT', message);
    },

    resetNotification({commit}){
        commit('RESET_NOTIFICATIONS')
    },

    async markNotificationAsSeen({commit}, {notif, sessionInfo}){
        const urlPath: string = `line/synapse/bot/${ConfigService.synapseBotId}/conversation/${sessionInfo.conversationId}/notification/${notif.id}/seen`;
        await toHttpRequest(ServerConnector.getBotServerConnectorInstance().post(urlPath,{}, ));
        commit('MARK_NOTIF_AS_SEEN', notif.id)
    },

    async updateCitations({ commit }, { botName, answerCounts }: { botName: string, answerCounts: number[] }): Promise<void> {
        const urlPath = `/line/synapse/bot/${ConfigService.synapseBotId}/updatecitationcount`;
        try {
            await toHttpRequest(
                ServerConnector.getBotServerConnectorInstance().post(urlPath, answerCounts)
            );
        } catch (err) {
            console.error(`Failed to register citation counts for bot: ${botName}`, err);
        }
    }
    


}